import { booleanAttribute, Component, computed, contentChildren, input, model, output } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { Dialog } from 'primeng/dialog';
import { PnDialogTemplate } from './directives/pn-dialog-template.directive';

export type PnDialogPosition = 'center' | 'top' | 'bottom' | 'left' | 'right' | 'topleft' | 'topright' | 'bottomleft' | 'bottomright';

@Component({
    selector: 'pn-dialog',
    imports: [Dialog, NgTemplateOutlet],
    templateUrl: './dialog.html',
    styleUrl: './dialog.scss',
})
export class PnDialog {
    /** Specifies the visibility of the dialog (two-way bindable) */
    visible = model<boolean>(false);

    /** Title text of the dialog */
    header = input<string>();

    /** Whether the dialog has a modal overlay. Default: true */
    modal = input(true, { transform: booleanAttribute });

    /** Whether the dialog is closable via the close icon. Default: true */
    closable = input(true, { transform: booleanAttribute });

    /** Whether pressing escape closes the dialog. Default: true */
    closeOnEscape = input(true, { transform: booleanAttribute });

    /** Whether clicking the modal backdrop closes the dialog. Default: false */
    dismissableMask = input(true, { transform: booleanAttribute });

    /** When true, all close routes (mask click, escape, close icon) are disabled. Use during an in-flight request. Default: false */
    busy = input(false, { transform: booleanAttribute });

    /** Whether the dialog is draggable. Default: false */
    draggable = input(false, { transform: booleanAttribute });

    /** Whether the dialog is resizable. Default: false */
    resizable = input(false, { transform: booleanAttribute });

    /** Whether the dialog can be maximized. Default: false */
    maximizable = input(false, { transform: booleanAttribute });

    /** Whether background scroll is blocked when dialog is visible. Default: true */
    blockScroll = input(true, { transform: booleanAttribute });

    /** Position of the dialog */
    position = input<PnDialogPosition>('center');

    /** Style class of the dialog */
    styleClass = input<string>();

    /** Callback when dialog is shown */
    shown = output<void>();

    /** Callback when dialog is hidden */
    hidden = output<void>();

    // Template support
    private templates = contentChildren(PnDialogTemplate);
    protected headerTemplate = computed(() => this.findTemplate('header'));
    protected footerTemplate = computed(() => this.findTemplate('footer'));
    protected headlessTemplate = computed(() => this.findTemplate('headless'));

    private findTemplate(name: string) {
        return this.templates().find((t) => t.name() === name)?.templateRef;
    }
}
